// app/javascript/controllers/lead_funnel_controller.js
import { Controller } from "@hotwired/stimulus"

// Default (German) validation messages; override per page via the
// data-lead-funnel-messages JSON attribute (e.g. Spanish funnels).
const VALIDATION_MESSAGES = {
  phoneRequired: "Bitte geben Sie eine Telefonnummer und Ländervorwahl ein.",
  phoneInvalid: "Bitte geben Sie eine gültige Telefonnummer ein (6-14 Ziffern).",
  emailRequired: "Bitte geben Sie eine E-Mail-Adresse ein.",
  emailInvalid: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
  zipRequired: "Bitte geben Sie eine Postleitzahl ein.",
  zipInvalid: "Die Postleitzahl muss genau fünf Ziffern lang sein.",
  nameRequired: "Bitte geben Sie Ihren Namen ein.",
  nameInvalid: "Der Name darf nur Buchstaben, Leerzeichen, Punkte und Bindestriche enthalten.",
  fieldRequired: "Bitte füllen Sie dieses Feld aus."
}

export default class extends Controller {
  static targets = [
    "backButton", "input", "phoneInput", "countryCode", "optionButton",
    "submitButton", "progressBar", "pillContainer", "circularImage",
    "errorMessage", "plainHeadline", "progressDot", "tapHint"
  ]

  connect() {
    this.boundRestrictInput = null
    this.boundRestrictPhoneInput = null
    this.loadPrefillData()
    this.loadAnswers()
    this.updateBackButtonVisibility()
    this.setupTurboStreamListener()
    this.updateProgressBar()
    this.updateAgentRepresentation()
    this.updateTapHint()
    this.setupInputRestrictions()
    this.setupPrefillDetection()
  }

  disconnect() {
    this.removeInputRestrictions()
    if (this._turboStreamHandler) {
      document.removeEventListener('turbo:before-stream-render', this._turboStreamHandler)
    }
  }

  preventFormSubmit(event) {
    event.preventDefault()
  }

  // Detect test environment (flag injected via layout)
  isTestEnv() {
    return document.documentElement.dataset.testEnv === 'true'
  }

  getCurrentFunnelUrl() {
    return window.location.pathname;
  }

  loadAnswers() {
    const funnelUrl = this.getCurrentFunnelUrl();
    try {
      const storedAnswers = localStorage.getItem(`leadFunnelAnswers_${funnelUrl}`);
      this.answers = storedAnswers ? JSON.parse(storedAnswers) : {};
    } catch (error) {
      console.error("Error loading answers from localStorage:", error);
      this.answers = {};
    }
  }

  checkForLoaderAndInitiateDelay() {
    const loaderElement = document.querySelector('[data-question-type="loader"]')
    // Handle each loader element exactly once — a stray re-invocation must not
    // spawn a second auto-advance (which could skip the following question).
    if (loaderElement && !loaderElement.dataset.loaderHandled) {
      loaderElement.dataset.loaderHandled = 'true'
      this.handleLoaderAutoAdvance(loaderElement)
    }
  }

  async handleLoaderAutoAdvance(loaderElement) {
    // Advance is driven by the loader animation finishing — the enhanced-loader
    // controller dispatches `enhanced-loader:complete` once its tiles, progress
    // ring and success message have all played — rather than a fixed dwell. That
    // way the animation always completes before we move on, regardless of how many
    // tiles the brand's loader renders. A safety timeout still guarantees we
    // advance if that event never fires.
    await this.waitForLoaderAnimation(loaderElement)
    this.navigateToStep(this.currentStep + 1, true)
  }

  // Resolves when the loader's cosmetic animation reports completion via the
  // `enhanced-loader:complete` event. In the test environment it resolves
  // immediately (the multi-second animation would blow spec timeouts); a safety
  // timeout resolves it anyway if the event never arrives, so the funnel can
  // never get permanently stuck on the loader.
  waitForLoaderAnimation(loaderElement) {
    if (this.isTestEnv()) return Promise.resolve()

    return new Promise((resolve) => {
      let settled = false
      const finish = () => {
        if (settled) return
        settled = true
        loaderElement.removeEventListener('enhanced-loader:complete', finish)
        clearTimeout(safety)
        resolve()
      }
      loaderElement.addEventListener('enhanced-loader:complete', finish, { once: true })
      // Comfortably longer than the longest loader animation (~6.5s).
      const safety = setTimeout(finish, 9000)
    })
  }

  setupTurboStreamListener() {
    // Store handler reference to prevent duplicates on reconnect
    if (this._turboStreamHandler) return
    this._turboStreamHandler = (event) => {
      if (event.target.action === "update" && event.target.target === "lead_funnel_question") {
        const originalRender = event.detail.render
        event.detail.render = (currentElement) => {
          originalRender(currentElement)
          // Defer to next frame so Stimulus MutationObserver can connect
          // new targets (phoneInputTarget, inputTarget, etc.) before we use them.
          setTimeout(() => {
            this._prefillDetectionInitialized = false
            this.populateInput()
            this.updateBackButtonVisibility()
            this.updateTapHint()
            this.checkForLoaderAndInitiateDelay()
            this.setupInputRestrictions()
            this.setupPrefillDetection()
          }, 0)
        }
      }
    }
    document.addEventListener('turbo:before-stream-render', this._turboStreamHandler)
  }

  setupInputRestrictions() {
    // Remove existing listeners before adding new ones to prevent duplicates
    this.removeInputRestrictions()

    // Bind and store references for cleanup
    this.boundRestrictInput = this.restrictInput.bind(this)
    this.boundRestrictPhoneInput = this.restrictPhoneInput.bind(this)

    if (this.hasInputTarget) {
      this.inputTarget.addEventListener('input', this.boundRestrictInput)
    }
    if (this.hasPhoneInputTarget) {
      this.phoneInputTarget.addEventListener('input', this.boundRestrictPhoneInput)
    }
  }

  removeInputRestrictions() {
    if (this.boundRestrictInput && this.hasInputTarget) {
      this.inputTarget.removeEventListener('input', this.boundRestrictInput)
    }
    if (this.boundRestrictPhoneInput && this.hasPhoneInputTarget) {
      this.phoneInputTarget.removeEventListener('input', this.boundRestrictPhoneInput)
    }
  }

  // Debounced to avoid killing iOS autofill — iOS aborts cascading fill if JS
  // modifies a field's value during the autofill pass (~100-200ms).
  restrictInput(event) {
    clearTimeout(this._restrictInputTimeout)
    this._restrictInputTimeout = setTimeout(() => {
      const input = event.target
      const inputType = input.getAttribute('data-input-type') || input.type

      switch (inputType) {
        case 'name':
          input.value = input.value.replace(/[^A-Za-zÀ-ÿ\s\-\.]/g, '')
          break
        case 'email':
          // Email validation is handled by the browser, no need for extra restrictions
          break
        case 'zip_code':
          input.value = input.value.replace(/\D/g, '').slice(0, 5)
          break
        case 'phone':
          this.restrictPhoneInput(event)
          break
        default:
          // No restrictions for other input types
          break
      }
    }, 300)
  }

  restrictPhoneInput(event) {
    const input = event.target
    input.value = input.value.replace(/\D/g, '').slice(0, 14)
  }

  get currentStep() {
    return parseInt(this.element.dataset.leadFunnelCurrentStep) || 0
  }

  set currentStep(value) {
    this.element.dataset.leadFunnelCurrentStep = value
  }

  get totalSteps() {
    return parseInt(this.element.dataset.leadFunnelTotalSteps) || 1
  }

  nextStep(event) {
    event.preventDefault()
    const button = event.currentTarget
    this.showSpinner(button)
    const submitValue = button.querySelector('[data-funnel-submit-value]').getAttribute('data-funnel-submit-value')
    this.saveAnswer(submitValue)
    this.navigateToStep(this.currentStep + 1, true)
  }

  nextStepWithInput(event) {
    event.preventDefault()
    if (this.validateInput()) {
      this.capturePrefillNow() // Final capture before leaving step
      this.showSpinner(this.submitButtonTarget)
      if (this.hasPhoneInputTarget) {
        const countryCode = this.hasCountryCodeTarget ? this.countryCodeTarget.value : "+49"
        const phoneNumber = this.phoneInputTarget.value
        this.saveAnswer({ countryCode, phoneNumber })
      } else {
        this.saveAnswer(this.inputTarget.value)
      }
      this.navigateToStep(this.currentStep + 1, true)
    }
  }

  previousStep(event) {
    event.preventDefault()
    this.navigateToStep(this.currentStep - 1, false)
  }

  updateInputValue(event) {
    this.saveAnswer(event.target.value)
  }

  // Debounced — iOS aborts cascading autofill if JS modifies values during the fill pass.
  updatePhoneNumber(event) {
    clearTimeout(this._phoneNumberTimeout)
    this._phoneNumberTimeout = setTimeout(() => {
      const phoneNumber = event.target.value.replace(/[^0-9]/g, '')
      event.target.value = phoneNumber
      const countryCode = this.hasCountryCodeTarget ? this.countryCodeTarget.value : "+49"
      this.saveAnswer({ countryCode, phoneNumber })
    }, 300)
  }


  handleKeyPress(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.nextStepWithInput(event)
    }
  }

  navigateToStep(step, isForward = true) {
    // In-flight guard: ignore overlapping navigations. currentStep lives in a DOM
    // attribute and is only updated ~200ms later, after the response renders, so
    // without this a double-submit (double-tap / Enter+click) or the loader's
    // auto-advance firing twice can race that update and skip a question — e.g.
    // jumping past the name step, producing a lead with contact details but no
    // name. Released only once the render has updated currentStep (renderStep /
    // catch below), so the next navigation reads the fresh value.
    if (this._navigating) return
    if (step >= 0 && step <= this.totalSteps) {
      this._navigating = true
      const url = this.element.dataset.leadFunnelNextStepUrl || `/lf/${this.element.dataset.leadFunnelSlug}/next_step`
      const data = new FormData()
      data.append('step', step)

      // Include session_id for embedded funnels
      const sessionId = this.element.dataset.leadFunnelSessionId
      if (sessionId) {
        data.append('session_id', sessionId)
      }

      // Dev/QA: forward the ?brand= preview override so the previewed brand
      // sticks across these turbo POSTs (the edge-cached GET can't set a session
      // cookie to persist it). No-op in production — Brandable#preview_brand
      // ignores it there.
      const brandPreview = new URLSearchParams(window.location.search).get('brand')
      if (brandPreview) {
        data.append('brand', brandPreview)
      }

      // Dev/QA: same forwarding for the ?aroundhome= broker-preview override.
      const aroundhomePreview = new URLSearchParams(window.location.search).get('aroundhome')
      if (aroundhomePreview) {
        data.append('aroundhome', aroundhomePreview)
      }

      if (isForward) {
        const answer = this.answers[this.currentStep]
        if (typeof answer === 'object') {
          if (answer.countryCode !== undefined) {
            // Phone answer
            data.append('answer[countryCode]', answer.countryCode || '')
            data.append('answer[phoneNumber]', answer.phoneNumber || '')
          }
        } else {
          data.append('answer', answer || '')
        }
      }

      fetch(url, {
        method: 'POST',
        headers: {
          "Accept": "text/vnd.turbo-stream.html, application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        credentials: 'same-origin',
        body: data
      })
        .then(response => {
          if (!response.ok) {
            return response.json().then(errorData => {
              throw new Error(errorData.error || 'An error occurred')
            })
          }
          return response.text()
        })
        .then(html => {
          const renderStep = () => {
            // Check if the response contains a redirection script
            if (html.includes('window.location.href')) {
              Turbo.renderStreamMessage(html) // This will execute the redirection script
            } else {
              Turbo.renderStreamMessage(html)
              this.currentStep = step
              history.pushState({ step }, '', location.href)
              this.updateProgressBar()
              this.updateAgentRepresentation()

              // Clear answers if we've reached the last step
              if (step === this.totalSteps) {
                this.clearAnswers();
              }

              // Release the in-flight guard now that currentStep reflects this
              // step, so the next navigation reads the updated value.
              this._navigating = false
            }
          }

          if (isForward) {
            const forwardDelay = this.isTestEnv() ? 0 : 200
            setTimeout(renderStep, forwardDelay)
          } else {
            renderStep()
          }
        })
        .catch(error => {
          console.error('Error:', error)
          this.showError(error.message)
          this.hideSpinner()
          this._navigating = false
        })
    }
  }

  updateAgentRepresentation() {
    if (this.hasPillContainerTarget && this.hasCircularImageTarget) {
      if (this.currentStep === 0) {
        this.pillContainerTarget.classList.remove('hidden');
        this.circularImageTarget.classList.add('hidden');
      } else if (this.currentStep === this.totalSteps) {
        this.pillContainerTarget.classList.add('hidden');
        this.circularImageTarget.classList.add('hidden');
      } else {
        this.pillContainerTarget.classList.add('hidden');
        this.circularImageTarget.classList.remove('hidden');
      }
    }

    // Update plain headline visibility
    if (this.hasPlainHeadlineTarget) {
      if (this.currentStep === this.totalSteps) {
        this.plainHeadlineTarget.classList.add('hidden');
      } else {
        this.plainHeadlineTarget.classList.remove('hidden');
      }
    }
  }

  updateBackButtonVisibility() {
    if (this.hasBackButtonTarget) {
      this.backButtonTarget.style.visibility = (this.currentStep === 0 || this.currentStep === this.totalSteps) ? 'hidden' : 'visible'
    }
  }

  // The Fokus layout shows a "tap an answer to continue" hint that only makes
  // sense on tap-to-advance multiple-choice steps — hide it on text-input, loader
  // and completion steps. Keyed off the rendered DOM (presence of option buttons)
  // so it tracks the actual question type after each turbo-stream swap. No-op in
  // layouts without the hint (the target only exists in Fokus).
  updateTapHint() {
    if (!this.hasTapHintTarget) return
    const isMultipleChoice = this.optionButtonTargets.length > 0
    this.tapHintTarget.classList.toggle('hidden', !isMultipleChoice)
  }

  saveAnswer(answer) {
    this.answers[this.currentStep] = answer;
    const funnelUrl = this.getCurrentFunnelUrl();
    try {
      localStorage.setItem(`leadFunnelAnswers_${funnelUrl}`, JSON.stringify(this.answers));
    } catch (error) {
      console.error("Error saving answers to localStorage:", error);
    }
  }

  populateInput() {
    if (this.hasInputTarget) {
      const savedAnswer = this.answers[this.currentStep]
      if (savedAnswer) {
        this.inputTarget.value = savedAnswer
      } else if (!this.inputTarget.value) {
        // Fall back to prefill data based on input type
        const inputType = this.inputTarget.dataset.inputType || this.inputTarget.getAttribute('autocomplete')
        if (inputType === 'name' && this.prefillData.name) this.inputTarget.value = this.prefillData.name
        if (inputType === 'email' && this.prefillData.email) this.inputTarget.value = this.prefillData.email
      }
    } else if (this.hasPhoneInputTarget && this.hasCountryCodeTarget) {
      const savedAnswer = this.answers[this.currentStep]
      if (savedAnswer && typeof savedAnswer === 'object' && savedAnswer.phoneNumber) {
        this.countryCodeTarget.value = savedAnswer.countryCode || ''
        this.phoneInputTarget.value = savedAnswer.phoneNumber
      } else if (!this.phoneInputTarget.value && this.prefillData.phone) {
        // Prefill phone from autocomplete capture
        const phone = this.prefillData.phone.replace(/\s+/g, '')
        // Strip country code prefix if present (e.g. +49, 0049)
        const stripped = phone.replace(/^(\+49|0049)/, '').replace(/^0/, '')
        this.phoneInputTarget.value = stripped
      }
    }
  }

  // V3: Detect browser autocomplete and capture values for future steps.
  // Captures from both companion fields (desktop Chrome) and visible form fields (iOS).
  // iOS "Kontakt ausfüllen" fills visible fields directly — we detect this via polling
  // since iOS may not fire input/change events on autofilled fields.
  setupPrefillDetection() {
    if (this._prefillDetectionInitialized) return
    const form = this.element.querySelector('[data-prefill-form]')
    if (!form) return
    this._prefillDetectionInitialized = true

    // Collect all capturable fields: companions (hidden) + visible inputs with autocomplete
    const allFields = form.querySelectorAll('[data-prefill-field], [autocomplete]')
    if (!allFields.length) return

    // Inject CSS keyframe for autofill detection (once per page)
    if (!document.getElementById('prefill-autofill-css')) {
      const style = document.createElement('style')
      style.id = 'prefill-autofill-css'
      style.textContent = '@keyframes prefillAutoFill { from {} to {} } input:-webkit-autofill { animation-name: prefillAutoFill !important; }'
      document.head.appendChild(style)
    }

    // 1. Standard event listeners (works on desktop Chrome/Firefox)
    allFields.forEach(field => {
      field.addEventListener('input', () => this.capturePrefillFromForm(form))
      field.addEventListener('change', () => this.capturePrefillFromForm(form))
      // 2. CSS animation listener — iOS fires animationstart on :-webkit-autofill
      field.addEventListener('animationstart', (e) => {
        if (e.animationName === 'prefillAutoFill') {
          setTimeout(() => this.capturePrefillFromForm(form), 50)
          setTimeout(() => this.capturePrefillFromForm(form), 200)
        }
      })
    })

    // 3. Aggressive polling — iOS may set values without any events.
    // Poll every 500ms for 5 seconds to catch delayed autofill.
    const pollInterval = setInterval(() => this.capturePrefillFromForm(form), 500)
    setTimeout(() => clearInterval(pollInterval), 5000)
  }

  // Load prefill data from sessionStorage (survives controller reconnects and turbo navigations).
  loadPrefillData() {
    const funnelUrl = this.getCurrentFunnelUrl()
    try {
      const stored = sessionStorage.getItem(`leadFunnelPrefill_${funnelUrl}`)
      this.prefillData = stored ? JSON.parse(stored) : {}
    } catch {
      this.prefillData = {}
    }
  }

  // Persist prefill data to sessionStorage after every capture.
  savePrefillData() {
    const funnelUrl = this.getCurrentFunnelUrl()
    try {
      sessionStorage.setItem(`leadFunnelPrefill_${funnelUrl}`, JSON.stringify(this.prefillData))
    } catch { /* quota exceeded — ignore */ }
  }

  // Capture prefill data from all fields in the form (companions + visible inputs).
  // Maps autocomplete attribute to prefillData key. Always overwrites with latest non-empty value.
  capturePrefillFromForm(form) {
    const autocompleteMap = { 'name': 'name', 'email': 'email', 'tel': 'phone' }
    let captured = false
    form.querySelectorAll('input[autocomplete]').forEach(field => {
      const autocomplete = field.getAttribute('autocomplete')
      const key = field.dataset.prefillField || autocompleteMap[autocomplete]
      if (!key) return
      const value = field.value?.trim()
      if (value) {
        this.prefillData[key] = value
        captured = true
      }
    })
    if (captured) this.savePrefillData()
  }

  // One-shot capture called at submit time — catches values the polling may have missed
  // (e.g. user triggered autofill after the 5-second polling window expired).
  capturePrefillNow() {
    const form = this.element.querySelector('[data-prefill-form]')
    if (form) this.capturePrefillFromForm(form)
  }

  showSpinner(button) {
    this.activeButton = button
    const spinner = button.querySelector('.spinner')
    const optionContent = button.querySelector('.option-content')
    if (spinner && optionContent) {
      optionContent.classList.add('hidden')
      spinner.classList.remove('hidden')
    }
  }

  hideSpinner() {
    if (this.activeButton) {
      const spinner = this.activeButton.querySelector('.spinner')
      const optionContent = this.activeButton.querySelector('.option-content')
      if (spinner && optionContent) {
        optionContent.classList.remove('hidden')
        spinner.classList.add('hidden')
      }
    }
  }

  updateProgressBar() {
    if (this.hasProgressBarTarget) {
      const newWidth = Math.min((this.currentStep / (this.totalSteps - 1)) * 100, 100);
      // Force a reflow to ensure the transition takes effect
      this.progressBarTarget.offsetWidth
      this.progressBarTarget.style.width = `${newWidth}%`
    }

    // Dot progress (Fokus layout). Guarded — absent in the control layout.
    if (this.hasProgressDotTarget) {
      this.progressDotTargets.forEach((dot, i) => {
        dot.classList.toggle('is-done', i < this.currentStep)
        dot.classList.toggle('is-active', i === this.currentStep)
      })
    }
  }

  validateInput() {
    if (this.hasPhoneInputTarget) {
      return this.validatePhoneNumber()
    }
    if (this.inputTarget.type === 'email') {
      return this.validateEmail()
    }
    if (this.inputTarget.type === 'tel' && (this.inputTarget.getAttribute('data-input-type') === 'zip_code' || this.inputTarget.placeholder === 'Postleitzahl')) {
      return this.validateZipCode()
    }
    if (this.inputTarget.getAttribute('data-input-type') === 'name') {
      return this.validateName()
    }
    return this.validateTextInput()
  }

  validatePhoneNumber() {
    const phoneNumber = this.phoneInputTarget.value.trim()
    const countryCode = this.hasCountryCodeTarget ? this.countryCodeTarget.value.trim() : "+49"
    if (phoneNumber.length === 0 || countryCode.length === 0) {
      this.showError(this.validationMessage('phoneRequired'))
      return false
    }
    if (!/^\d{6,14}$/.test(phoneNumber)) {
      this.showError(this.validationMessage('phoneInvalid'))
      return false
    }
    this.hideError()
    return true
  }

  validateEmail() {
    const email = this.inputTarget.value.trim()
    if (email.length === 0) {
      this.showError(this.validationMessage('emailRequired'))
      return false
    }
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
      this.showError(this.validationMessage('emailInvalid'))
      return false
    }
    this.hideError()
    return true
  }

  validateZipCode() {
    const zipCode = this.inputTarget.value.trim()
    if (zipCode.length === 0) {
      this.showError(this.validationMessage('zipRequired'))
      return false
    }
    if (!/^\d{5}$/.test(zipCode)) {
      this.showError(this.validationMessage('zipInvalid'))
      return false
    }
    this.hideError()
    return true
  }

  validateName() {
    const name = this.inputTarget.value.trim()
    if (name.length === 0) {
      this.showError(this.validationMessage('nameRequired'))
      return false
    }
    if (!/^[A-Za-zÀ-ÿ\s\-\.]+$/.test(name)) {
      this.showError(this.validationMessage('nameInvalid'))
      return false
    }
    this.hideError()
    return true
  }

  validateTextInput() {
    const value = this.inputTarget.value.trim()
    if (value.length === 0) {
      this.showError(this.validationMessage('fieldRequired'))
      return false
    }
    this.hideError()
    return true
  }

  validationMessage(key) {
    if (!this._validationMessages) {
      const overrides = this.element.dataset.leadFunnelMessages
      this._validationMessages = { ...VALIDATION_MESSAGES, ...(overrides ? JSON.parse(overrides) : {}) }
    }
    return this._validationMessages[key]
  }

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove('hidden')
    }
  }

  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add('hidden')
    }
  }

  clearAnswers() {
    this.answers = {}
    this.prefillData = {}
    const funnelUrl = this.getCurrentFunnelUrl()
    localStorage.removeItem(`leadFunnelAnswers_${funnelUrl}`)
    sessionStorage.removeItem(`leadFunnelPrefill_${funnelUrl}`)
  }

}
